import { useEffect, useMemo } from 'react';
import { PrimeMode, PrimeQuotaResult } from '@stores/prime-quota';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { readCookie } from '@utils/cookies';
import usePlusIconicSubscribed from '@hooks/usePlusIconicSubscribed';

interface UsePrimeQuotaReturn {
  isQuotaExhausted: boolean;
  unlockedVideosUids: string[];
  primeVideoQuota: PrimeQuotaResult;
}

const usePrimeVideoQuota = (
  goalUID: string | undefined
): UsePrimeQuotaReturn => {
  const fetchQuotaDetails = usePrimeQuotaStore(
    (state) => state.fetchQuotaDetails
  );
  const fetchUnlockedVideosUids = usePrimeQuotaStore(
    (state) => state.fetchUnlockedVideosUids
  );
  const primeVideoQuota = usePrimeQuotaStore((state) =>
    state.getQuotaForMode(PrimeMode.VIDEO)
  );
  const unlockedVideosUids = usePrimeQuotaStore(
    (state) => state.unlockedVideosUids
  );

  const isSubscribed = usePlusIconicSubscribed({ goalUID });
  const accessToken = readCookie('accessToken');
  const isLoggedIn = Boolean(accessToken);

  useEffect(() => {
    if (goalUID && isLoggedIn) {
      fetchQuotaDetails({ goalUID, accessToken });
      fetchUnlockedVideosUids({ goalUID, accessToken });
    }
  }, [
    goalUID,
    accessToken,
    isLoggedIn,
    fetchQuotaDetails,
    fetchUnlockedVideosUids
  ]);

  const isQuotaExhausted = useMemo(() => {
    // If user is not logged in, don't show quota exhausted
    if (!isLoggedIn) return false;
    if (isSubscribed) return false;
    return primeVideoQuota.isQuotaExhausted;
  }, [primeVideoQuota, isSubscribed, isLoggedIn]);

  return { isQuotaExhausted, unlockedVideosUids, primeVideoQuota };
};

export default usePrimeVideoQuota;
